import React, {type ReactNode} from 'react';
import {Radio as BaseRadio} from 'reakit/Radio';
import {Checkbox as BaseCheckbox} from 'reakit/Checkbox';

import {emptyObject} from '@packages/utilities/fp/utils';
import {useSize} from '@packages/utilities/styled';
import {useUniqueId} from '@packages/systems/spring-hooks';

import {
  getDefinedValue,
  mergeRefs,
  ZERO_WIDTH_SPACE_CHARACTER,
} from '../../utils';
import {useRadioGroup} from '../RadioGroup';
import {RadioButton} from '../Radio';
import {HStack, VStack} from '../Stack';
import {Spacer} from '../Spacer';
import {Text} from '../Text';

import {
  LabelTextView,
  RadioCardView,
  RadioWrapperView,
  RadioInputView,
} from './RadioCard.styles';

import type {CardProps} from '../Card/Card';
import type {RadioProps} from '../Radio/Radio';
import type {
  HTMLAttributes,
  SizeContextSize,
  SpringTooling,
} from '../__SharedTypes';

export type RadioCardProps = CardProps &
  RadioProps & {
    /**
     * Aligns the indicator to the start or end of the RadioCard.
     */
    alignIndicator?: 'start' | 'end' | 'end-absolute';

    /**
     * Renders the style of the checked indicator.
     */
    checkedIndicator?: 'checkbox' | 'radio';
    /**
     * The color variant for the RadioCard indicator.
     */
    colorVariant?: 'blue' | 'purple' | 'green';
    /**
     * The content of the RadioCard. Use to provide description or any content related to the RadioCard
     */
    children?: ReactNode;
    /**
     * Adds a background color to the RadioCard when it's hovered or checked.
     */
    emphasizeBackground?: boolean;
    /**
     * Renders the disabled state for the RadioCard.
     */
    isDisabled?: boolean;
    /**
     * Renders the error state for the RadioCard.
     */
    isError?: boolean;
    /**
     * Whether the Card and its contents should fill the height of the parent.
     */
    isFullHeight?: boolean;
    /**
     * Disables the focus when clicking on the RadioCard's body, allowing for interactions
     * with controls within the description / children.
     */
    isDescriptionInteractive?: boolean;
    /**
     * The label text for the input. If omitted, provide `aria-label`
     */
    label?: string | ReactNode;
    /**
     * Render prop to render a custom input indicator. Defaults to Spring RadioButton.
     * The render runction will be passed an object with shape:
     * { isChecked: boolean, isError: boolean, size: SizeContextSize}
     */
    renderIndicator?: (arg1: {
      checkedIndicator: RadioCardProps['checkedIndicator'];
      colorVariant?: RadioCardProps['colorVariant'];
      isChecked?: boolean;
      isDisabled?: boolean;
      isError?: boolean;
      size: SizeContextSize;
    }) => ReactNode;
    /**
     * When true, adds a blue border to the Card indicating the input is currently selected/checked
     */
    shouldHighlightSelected?: boolean;
    /**
     * Renders as either a radio or checkbox input. Checkboxes can be used to multi-select options.
     */
    type?: 'checkbox' | 'radio';
  } & HTMLAttributes &
  SpringTooling;

/**
 * # RadioCard
 *
 * RadioCard is a form component that enables the user to select a single item
 * within a list of options that's presented with a Card UI.
 *
 * ## Usage
 *
 * ```jsx
 * import {RadioGroup, RadioCard} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   const [value, setValue] = React.useState('crocus');
 *   return (
 *     <RadioGroup aria-label="Choose a flower" value={value} onChange={setValue}>
 *       <RadioCard value="crocus">Crocus</RadioCard>
 *       <RadioCard value="daffodils">Daffodils</RadioCard>
 *       <RadioCard value="epimedium">Epimedium</RadioCard>
 *     </RadioGroup>
 *   );
 * };
 * ```
 *
 * @component
 */
export const RadioCard = React.forwardRef<HTMLElement, RadioCardProps>(
  (
    {
      alignIndicator = 'start',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      checkedIndicator = 'checkbox',
      children,
      colorVariant = 'blue',
      emphasizeBackground,
      id,
      isChecked: isCheckedProp,
      isDescriptionInteractive = false,
      isDisabled = false,
      isError: isErrorProp,
      isFullHeight,
      label = '',
      name: nameProp,
      // @ts-expect-error - TS2322 - Type 'null' is not assignable to type '(arg1: { isChecked: boolean; isError: boolean; size: SizeContextSize; }) => ReactNode'.
      renderIndicator = null,
      shouldHighlightSelected = false,
      size: sizeProp,
      type = 'radio',
      value,
      ...props
    }: RadioCardProps,
    ref
  ) => {
    const size = useSize(sizeProp);
    const {isError: isErrorContext, name, radio} = useRadioGroup();
    const isError = getDefinedValue(isErrorProp, isErrorContext);
    const isRadio = type === 'radio';
    const inputId = useUniqueId(RadioCard, 'input');
    const inputRef = React.useRef<HTMLElement | null>(null);
    const labelId = useUniqueId(RadioCard, 'label');
    const labelRef = React.useRef<HTMLElement | null>(null);
    const descriptionId = useUniqueId(RadioCard, 'description');
    const descriptionRef = React.useRef<HTMLElement | null>(null);
    const isExplicitlyLabelled = ariaLabel != null || ariaLabelledBy != null;
    const isControlled =
      isCheckedProp != null || Object.keys(radio).includes('state');
    const [isChecked, setIsChecked] = React.useState(false);

    const InputComponent = isRadio ? BaseRadio : BaseCheckbox;
    /**
     * Reakit doesn't provide a useCheckbox state (vs. state from useRadioGroup).
     * If the type isCheckbox, then we can send in an empty object.
     */
    const baseInputComponentProps = isRadio ? radio : emptyObject;
    const inputComponentProps = {
      checked: isControlled ? isCheckedProp : isChecked,
      onChange: () => setIsChecked(true),
      disabled: isDisabled,
      id,
      value,
    } as const;

    // @ts-expect-error - TS7031 - Binding element 'target' implicitly has an 'any' type.
    const handleOnWrapperClick = ({target, ...event}) => {
      if (event.defaultPrevented) return;

      // Ignore triggering input if target is:
      // * within label (default browser behavior)
      // * description clicked is interactive
      // * or is in a Portal outside the container
      if (
        inputRef.current?.contains(target) ||
        labelRef.current?.contains(target) ||
        (isDescriptionInteractive &&
          (descriptionRef.current?.contains(target) ||
            !event.currentTarget.contains(target)))
      ) {
        return;
      }

      // Allowing the focus to be maintained on the input element. If label exists, trigger
      // click event to emulate focus. Otherwise, click and focus on input. This enables
      // the :focus-within styles to render correctly.
      window.requestAnimationFrame(() => {
        if (labelRef.current && typeof labelRef.current.click === 'function') {
          labelRef.current?.click();
        } else {
          // Note: This may cause weird focus-visible styles to behave oddly in some browsers,
          // so we use this method as a fallback if label does not exist. See Jira - DS-1342
          inputRef.current?.click();
          inputRef.current?.focus();
        }
      });
    };

    const renderIndicatorFunction = React.useMemo(() => {
      if (
        typeof renderIndicator === 'undefined' ||
        typeof renderIndicator !== 'function'
      ) {
        return DefaultInput;
      }
      return renderIndicator;
    }, [renderIndicator]);

    return (
      // @ts-expect-error - TS2604 - JSX element type 'InputComponent' does not have any construct or call signatures.
      <InputComponent {...baseInputComponentProps} {...inputComponentProps}>
        {/* @ts-expect-error - TS7006 - Parameter 'inputProps' implicitly has an 'any' type. */}
        {(inputProps) => {
          const mergedRefs = mergeRefs([inputProps.ref, inputRef, ref]);
          const radioName = getDefinedValue(nameProp, name, inputProps.name);

          // Always call renderIndicator to maintain hook consistency
          const indicatorContent = renderIndicatorFunction({
            checkedIndicator,
            colorVariant,
            isChecked: inputProps.checked,
            isDisabled,
            isError,
            size,
          });

          const indicator = (
            <RadioWrapperView
              isAbsolutelyPositioned={alignIndicator === 'end-absolute'}
            >
              <RadioInputView
                {...inputProps}
                aria-describedby={descriptionId}
                aria-label={ariaLabel}
                aria-labelledby={
                  isExplicitlyLabelled ? ariaLabelledBy : labelId
                }
                checked={undefined}
                id={inputId}
                name={radioName}
                ref={mergedRefs}
                type={type}
              />
              {indicatorContent}
              <TextAlignmentSpacer />
            </RadioWrapperView>
          );

          return (
            <RadioCardView
              height={isFullHeight ? '100%' : undefined}
              {...props}
              colorVariant={colorVariant}
              emphasizeBackground={emphasizeBackground}
              isDisabled={isDisabled}
              isError={isError}
              onPointerDown={handleOnWrapperClick}
              shouldHighlightSelected={shouldHighlightSelected}
              size={size}
            >
              <HStack align="start" height={isFullHeight ? '100%' : undefined}>
                {alignIndicator === 'start' ? indicator : null}
                <Spacer data-sc="RadioCardContent">
                  <VStack
                    height={isFullHeight ? '100%' : undefined}
                    spacing={0}
                  >
                    {label ? (
                      <LabelTextView
                        as="label"
                        hasDescription={Boolean(children)}
                        // We are ignoring this ts-error until we can get polymorphic types working.
                        // @ts-expect-error - TS2769 - No overload matches this call.
                        htmlFor={inputId}
                        id={labelId}
                        ref={labelRef}
                      >
                        {label}
                      </LabelTextView>
                    ) : null}
                    {children ? (
                      <Text
                        height={isFullHeight ? '100%' : undefined}
                        id={descriptionId}
                        ref={descriptionRef}
                      >
                        {children}
                      </Text>
                    ) : null}
                  </VStack>
                </Spacer>
                {alignIndicator === 'end' ? indicator : null}
              </HStack>
              {alignIndicator === 'end-absolute' ? indicator : null}
            </RadioCardView>
          );
        }}
      </InputComponent>
    );
  }
);

const TextAlignmentSpacer = () => (
  <LabelTextView>{ZERO_WIDTH_SPACE_CHARACTER}</LabelTextView>
);

const DefaultInput = ({
  checkedIndicator,
  colorVariant,
  isChecked,
  isError,
  isDisabled,
  size,
}: Pick<
  RadioCardProps,
  | 'checkedIndicator'
  | 'colorVariant'
  | 'isChecked'
  | 'isError'
  | 'isDisabled'
  | 'size'
>) => {
  return (
    <RadioButton
      checkedIndicator={checkedIndicator}
      colorVariant={colorVariant}
      isChecked={isChecked}
      isDisabled={isDisabled}
      isError={isError}
      // shouldBypassAria: We are using the isChecked, isDisabled, and isIndeterminate props for styles,
      // but we don't actually want to add `aria-checked`, `aria-disabled`, or `data-indeterminate` attributes
      // to this <div> component. The RadioInputView component provides an actual <input type="radio" />
      // with all the necessary semantic markup in the HTML.
      shouldBypassAria
      size={size}
    />
  );
};

RadioCard.displayName = 'RadioCard';
RadioCard.toString = RadioCardView.toString;

export default RadioCard;
